import React, { useState } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import CustomPopover from "../customPopover";
import DateRangeSelect from "../../layout/common/date-range-select";

export function FilterPopover({
  open,
  onClose,
  title = "Select Filters",
  arrow = "top-right",
  fields,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  maxWidth = 450,
  minWidth = 360,
  showApplyButton = false,
  showClearButton = false,
  accordionMode = false,
  defaultFilters = {},
}) {
  const [expandedSections, setExpandedSections] = useState(
    fields[0]?.name || ""
  );

  const handleFieldChange = (fieldName) => (value) => {
    const newFilters = { ...filters, [fieldName]: value };
    onFiltersChange(newFilters);
  };

  const handleSelectChange = (fieldName) => (event) => {
    handleFieldChange(fieldName)(event.target.value);
  };

  const handleTextChange = (fieldName) => (event) => {
    handleFieldChange(fieldName)(event.target.value);
  };

  const handleCheckboxChange = (fieldName) => (event) => {
    handleFieldChange(fieldName)(event.target.checked);
  };

  const handleAutocompleteChange = (fieldName, multiple) => (event, value) => {
    if (multiple) {
      handleFieldChange(fieldName)(value);
    } else {
      handleFieldChange(fieldName)(value?.value || "");
    }
  };

  const handleSectionToggle = (sectionName) => {
    setExpandedSections((prev) => (prev === sectionName ? "" : sectionName));
  };

  const isExpanded = (sectionName) => expandedSections === sectionName;

  const handleApply = () => {
    console.log('filters: ', filters);
    onApply?.(filters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {};
    fields.forEach((field) => {
      if (field.type === "checkbox" || field.type === "switch") {
        clearedFilters[field.name] = false;
      } else if (field.type === "date-range") {
        clearedFilters[field.name] = null;
      } else if (field.multiple) {
        clearedFilters[field.name] = [];
      } else {
        clearedFilters[field.name] = "";
      }
    });
    onFiltersChange({...clearedFilters, ...defaultFilters });
    onClear?.();
  };

  const renderField = (field) => {
    const value =
      filters[field.name] ||
      (field.multiple
        ? []
        : field.type === "checkbox" || field.type === "switch"
        ? false
        : "");

    switch (field.type) {
      case "select":
        return (
          <FormControl fullWidth>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={handleSelectChange(field.name)}
              input={<OutlinedInput label={field.label} />}
              multiple={field.multiple}
            >
              {!field.required && !field.multiple && (
                <MenuItem value="">All {field.label}</MenuItem>
              )}
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "text":
        return (
          <TextField
            fullWidth
            label={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={handleTextChange(field.name)}
            variant="outlined"
          />
        );

      case "date":
        return (
          <TextField
            fullWidth
            label={field.label}
            type="date"
            value={value}
            onChange={handleTextChange(field.name)}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        );

      case "autocomplete":
        return (
          <Autocomplete
            fullWidth
            multiple={field.multiple}
            options={field.options || []}
            getOptionLabel={(option) => option.label}
            value={
              field.multiple
                ? field.options?.filter((opt) => value.includes(opt.value)) ||
                  []
                : field.options?.find((opt) => opt.value === value) || null
            }
            onChange={handleAutocompleteChange(field.name, field.multiple)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                placeholder={field.placeholder}
                variant="outlined"
              />
            )}
          />
        );

      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value}
                onChange={handleCheckboxChange(field.name)}
              />
            }
            label={field.label}
          />
        );

      case "switch":
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value}
                onChange={handleCheckboxChange(field.name)}
              />
            }
            label={field.label}
          />
        );

      case "radio":
        return (
          <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {field.label}
            </Typography>
            <RadioGroup value={value} onChange={handleSelectChange(field.name)}>
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case "date-range":
        return (
          <DateRangeSelect
            dateRange={value}
            onFilterChange={(newValue) =>
              handleFieldChange(field.name)(newValue)
            }
          />
        );

      default:
        return null;
    }
  };

  const renderAccordionField = (field) => {
    const value = filters[field.name] || "";

    const getValueLabel = () => {
      if (field.type === "date-range") {
        const start = value?.startDate
          ? typeof value.startDate === "string"
            ? value.startDate
            : value.startDate.format("DD/MM/YY")
          : "";
        const end = value?.endDate
          ? typeof value.endDate === "string"
            ? value.endDate
            : value.endDate.format("DD/MM/YY")
          : "";
        return start || end ? `${start}-${end}` : "";
      }

      return typeof value === "object" ? JSON.stringify(value) : value;
    };

    return (
      <Accordion
        key={field.name}
        expanded={isExpanded(field.name)}
        onChange={() => handleSectionToggle(field.name)}
        disableGutters
        sx={{
          borderRadius: 1,
          boxShadow: "none",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {field.label}
            </Typography>
            {!isExpanded(field.name) && (
              <Typography variant="subtitle2" color="text.secondary">
                {typeof value === "boolean"
                  ? value
                    ? "Yes"
                    : "No"
                  : getValueLabel()}
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>{renderField(field)}</AccordionDetails>
      </Accordion>
    );
  };

  return (
    <CustomPopover
      open={open}
      onClose={onClose}
      arrow={arrow}
      sx={{ maxWidth, minWidth: { xs: 240, sm: minWidth }, maxHeight: "80vh" }}
    >
      <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography
          variant="subtitle2"
          sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterListIcon /> {title}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {accordionMode ? (
          <Stack spacing={1}>{fields.map(renderAccordionField)}</Stack>
        ) : (
          <Stack spacing={2}>
            {fields.map((field) => (
              <Box key={field.name}>{renderField(field)}</Box>
            ))}
          </Stack>
        )}
      </Box>

      {(showApplyButton || showClearButton) && (
        <Stack
          direction="row"
          gap={2}
          justifyContent="flex-end"
          sx={{
            px: 2,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {showClearButton && (
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          {showApplyButton && (
            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          )}
        </Stack>
      )}
    </CustomPopover>
  );
}
